import {
    Avatar,
    Box,
    Card,
    Checkbox,
    Fab,
    Grid,
    Hidden,
    Icon,
    IconButton, MenuItem,
    styled,
    useTheme,
} from '@mui/material';
import { Button, Input, List, message, Modal, Tag } from 'antd';
import { Span } from '../../../components/Typography';
import React, { Fragment, useState } from 'react';
import { Link } from "react-router-dom";
import { MatxMenu } from "../../../components";
import useDashboard from "../../../hooks/useDashboard";
import { useAllProductList } from '../../../requests/products';
import { useUpdateOrderProducts } from '../../../requests/order';

const ProjectName = styled(Span)(({ theme }) => ({
    marginLeft: 24,
    fontWeight: '500',
    [theme.breakpoints.down('sm')]: { marginLeft: 4 },
}));

const StyledItem = styled(MenuItem)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    minWidth: 200,
    '& a': {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
    },
    '& span': { marginRight: '10px', color: theme.palette.text.primary },
}));

const StyledAvatar = styled(Avatar)(() => ({
    width: '32px !important',
    height: '32px !important',
}));

const emptyForm = { product_id: null, product_title: '', quantity: 1, price: 0, color: '', size: '' };

const OrderItemsCards = ({ products, orderId, loadData }) => {
    const { palette } = useTheme();
    const textMuted = palette.text.secondary;

    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [search, setSearch] = useState('');

    const { data: allProductsData } = useAllProductList({ search });
    const { mutate: updateOrderProducts, isLoading: saving } = useUpdateOrderProducts();

    const allProducts = allProductsData?.all_products || [];

    function openAdd() {
        setForm(emptyForm);
        setSearch('');
        setAddOpen(true);
    }

    function pickProduct(p) {
        setForm({
            product_id: p.id,
            product_title: p.product_title,
            quantity: 1,
            price: p.sale_price > 0 ? p.sale_price : p.price,
            color: p.color && String(p.color).toLowerCase() !== 'n/a' ? p.color : '',
            size: p.size && String(p.size).toLowerCase() !== 'n/a' ? p.size : '',
        });
    }

    function submitAdd() {
        if (!form.product_id) {
            message.error('Pick a product first');
            return;
        }
        const payload = {
            product_id: form.product_id,
            quantity: Number(form.quantity) || 1,
            price: Number(form.price) || 0,
            color: form.color || '',
            size: form.size || '',
        };
        updateOrderProducts({ id: orderId, products: [payload] }, {
            onSuccess: () => {
                message.success('Product added to order');
                setAddOpen(false);
                loadData();
            },
            onError: (err) => {
                message.error(err?.message || 'Failed to add product');
            }
        });
    }

    function openEdit(index) {
        const p = products[index];
        setForm({
            product_id: p.product_id,
            product_title: p.product_title,
            quantity: p.quantity,
            price: p.price,
            color: p.color && String(p.color).toLowerCase() !== 'n/a' ? p.color : '',
            size: p.size && String(p.size).toLowerCase() !== 'n/a' ? p.size : '',
        });
        setEditIndex(index);
        setEditOpen(true);
    }

    function submitEdit() {
        const payload = {
            product_id: form.product_id,
            quantity: Number(form.quantity) || 1,
            price: Number(form.price) || 0,
            color: form.color || '',
            size: form.size || '',
        };
        updateOrderProducts({ id: orderId, products: [payload] }, {
            onSuccess: () => {
                message.success('Product updated');
                setEditOpen(false);
                loadData();
            },
            onError: (err) => {
                message.error(err?.message || 'Failed to update product');
            }
        });
    }

    return (
        <Fragment>
            <Box display="flex" justifyContent="flex-end" mb={1}>
                <Button type="primary" icon={<span>+</span>} onClick={openAdd}>Add Product</Button>
            </Box>

            {(products || []).map((productItem, id) => (
                <Fragment key={id}>
                    <Card sx={{ py: 1, px: 2 }} className="project-card">
                        <Grid container alignItems="center">
                            <Grid item md={5} xs={7}>
                                <Box display="flex" alignItems="center">
                                    <Hidden smDown>
                                        <StyledAvatar src={productItem.image?.image ?? ""} />
                                    </Hidden>
                                    <ProjectName>{productItem.product_title}</ProjectName>
                                </Box>
                            </Grid>

                            <Grid item md={3} xs={7}>
                                <Box display="flex" alignItems="center">
                                    <Span>{productItem.color?.toLowerCase() === "n/a" ? "Default" : productItem.color ?? "Default"}-{productItem.size?.toLowerCase() === 'n/a' ? "" : productItem.size ?? ""}</Span>
                                </Box>
                            </Grid>

                            <Grid item md={2} xs={4}>
                                <Box color={textMuted}>৳{productItem.price}</Box>
                            </Grid>

                            <Hidden smDown>
                                <Grid item xs={1}>
                                    <Box display="flex" position="relative" marginLeft="-0.875rem !important">
                                        Qty: {productItem.quantity}
                                    </Box>
                                </Grid>
                            </Hidden>

                            <Grid item xs={1}>
                                <Box display="flex" justifyContent="flex-end" alignItems="center">
                                    <Link to={"/product/detail/" + productItem.product_id}>
                                        <IconButton color='primary'>
                                            <Icon>remove_red_eye</Icon>
                                        </IconButton>
                                    </Link>
                                    <IconButton color='primary' onClick={() => openEdit(id)}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>
                    <Box py={1} />
                </Fragment>
            ))}

            <Modal
                title="Add Product to Order"
                open={addOpen}
                onCancel={() => setAddOpen(false)}
                okText="Add"
                confirmLoading={saving}
                width={700}
                onOk={submitAdd}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Input
                        placeholder="Search products..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 8 }}>
                        {allProducts.length === 0 ? (
                            <div style={{ padding: 12, color: '#999', textAlign: 'center' }}>No products found</div>
                        ) : allProducts.map(p => (
                            <div
                                key={p.id}
                                onClick={() => pickProduct(p)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    background: form.product_id === p.id ? '#e6f7ff' : 'transparent',
                                    borderRadius: 4,
                                }}
                            >
                                {p.product_title}
                            </div>
                        ))}
                    </div>

                    {form.product_id && (
                        <>
                            <Tag color="blue">{form.product_title}</Tag>
                            <Input addonBefore="Quantity" type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
                            <Input addonBefore="Price" type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                            <Input addonBefore="Color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                            <Input addonBefore="Size" value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} />
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                title="Edit Product"
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                okText="Save"
                confirmLoading={saving}
                onOk={submitEdit}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Tag color="blue">{form.product_title}</Tag>
                    <Input addonBefore="Quantity" type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
                    <Input addonBefore="Price" type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                    <Input addonBefore="Color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                    <Input addonBefore="Size" value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} />
                </div>
            </Modal>
        </Fragment>
    );
};

export default OrderItemsCards;
